import { type AlertProps, useAlert } from '@ads/components-react';
import { useTranslation } from 'react-i18next';

import { FileUpload } from '@/shared/ui/atoms';

import type {
  RejectedFile,
  UploadOutcome,
  UploadProgressItem,
} from '../../../hooks/useTutorDocumentUpload';
import { formatCount } from '../../../utils/format';
import { CONTEXT_FILE_ACCEPT, CONTEXT_FILE_MAX_SIZE_MB } from '../../../utils/tutorAiLimits';
import { SurfaceCard } from '../../molecules/SurfaceCard';
import { UploadProgressList } from '../../molecules/UploadProgressList';

/**
 * Ajuste de quebra de linha dos toasts que citam nome de arquivo.
 *
 * Nome de arquivo é um token só, sem espaço onde quebrar, e passa fácil da largura útil do
 * toast (viewport de 380px no desktop, menos ícone, botão de fechar e padding — sobram
 * ~290px, contra ~310px de `mba_agronegocios_usp_esalq_resumo.md` em `ts-subtitle-xs`, que é
 * 16px). Sublinhado não é ponto de quebra para o navegador, então o texto vazava da caixa e
 * aparecia cortado.
 *
 * As duas classes são necessárias: `break-words` autoriza quebrar dentro da palavra quando
 * ela não couber, e `min-w-none` libera o `contentWrapper` — que o Alert declara `flex-1`
 * sem `min-width: 0` — a encolher abaixo do próprio min-content. Só uma delas ainda deixaria
 * o flex item medir pelo token inteiro.
 */
// `NonNullable`: com `exactOptionalPropertyTypes`, um `custom` que possa ser `undefined` não
// é atribuível à prop opcional do toast.
const ALERT_WRAP_LONG_NAMES: NonNullable<AlertProps['custom']> = {
  contentWrapper: 'min-w-none',
  title: 'break-words',
};

export interface ContextFileUploadProps {
  /** Envia os arquivos e devolve os recusados no cliente mais o consolidado do lote. */
  readonly onUpload: (
    files: File[],
  ) => Promise<{ rejected: readonly RejectedFile[]; outcome: UploadOutcome }>;
  readonly isUploading: boolean;
  /** Todos os arquivos do lote atual com sua situação, para o feedback visual do envio. */
  readonly items?: readonly UploadProgressItem[];
}

/**
 * Área de upload de documentos de contexto da turma.
 *
 * O upload indexa automaticamente. Enquanto o lote está em andamento, `items` alimenta o
 * feedback visual em tempo real: uma linha por arquivo aceito, com percentual de upload,
 * indicador de indexação ou situação terminal — sem ele o operador ficava sem nenhum sinal
 * entre soltar o arquivo e o toast final. Esse toast final continua existindo e reporta
 * chunks e tokens de embedding consumidos, para que o custo da operação seja visível mesmo
 * sem confirmação prévia.
 *
 * Recusa de cliente sai um toast por arquivo, com o nome: mensagem genérica não diz qual
 * dos arquivos soltos foi rejeitado.
 *
 * O atom `FileUpload` compartilhado não aceita uma prop `disabled` — em vez de adicionar
 * uma só para este caso, o bloqueio durante o envio é feito aqui: o handler ignora novos
 * arquivos enquanto `isUploading` for `true` e avisa, via toast informativo, que o envio
 * atual precisa terminar antes — silenciosamente descartar os arquivos soltos pareceria um
 * bug tanto quanto a falta de feedback que esta mudança corrige.
 */
export function ContextFileUpload({ onUpload, isUploading, items = [] }: ContextFileUploadProps) {
  const { t } = useTranslation('tutorAI');
  const { alert } = useAlert();

  /** Todo toast daqui pode citar nome de arquivo — a quebra vale para todos, não só a recusa. */
  function notify(alertType: AlertProps['alertType'], title: string) {
    alert({ alertType, title, custom: ALERT_WRAP_LONG_NAMES });
  }

  function rejectionTitle({ name, reason }: RejectedFile): string {
    switch (reason) {
      case 'type':
        return t('classDetail.upload.rejectedType', { name });
      case 'size':
        return t('classDetail.upload.rejectedSize', { name, size: CONTEXT_FILE_MAX_SIZE_MB });
      case 'duplicate':
        return t('classDetail.upload.rejectedDuplicate', { name });
    }
  }

  async function handleAddFiles(files: File[]) {
    if (isUploading) {
      notify('information', t('classDetail.upload.batchInProgress'));
      return;
    }

    const { rejected, outcome } = await onUpload(files);

    for (const item of rejected) {
      notify('warning', rejectionTitle(item));
    }

    for (const name of outcome.uploadFailures) {
      notify('danger', t('classDetail.upload.uploadFailed', { name }));
    }

    for (const name of outcome.importFailures) {
      notify('warning', t('classDetail.upload.importFailed', { name }));
    }

    if (outcome.importedCount > 0) {
      notify(
        'success',
        t('classDetail.upload.imported', {
          count: outcome.importedCount,
          chunks: formatCount(outcome.chunksGenerated),
          tokens: formatCount(outcome.embedTokensUsed),
        }),
      );
    }
  }

  return (
    <SurfaceCard
      title={t('classDetail.upload.sectionTitle')}
      description={t('classDetail.upload.description')}
    >
      <div className="flex flex-col gap-xxs p-xxs">
        <FileUpload
          files={[]}
          addFiles={files => void handleAddFiles(files)}
          removeFile={() => undefined}
          hasMultipleFiles
          accept={CONTEXT_FILE_ACCEPT}
          helper={t('classDetail.upload.helper', { size: CONTEXT_FILE_MAX_SIZE_MB })}
        />
        <UploadProgressList items={items} />
      </div>
    </SurfaceCard>
  );
}
